import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import * as H from 'history'
import {
  requestValidationInterceptor,
  responseValidationInterceptor,
  errorValidationInterceptor
} from './interceptors/schemaValidation'

// get apiURL from ENV
const apiUrl = import.meta.env.MODE === 'production' ? import.meta.env.VITE_PROD_API_URL : import.meta.env.VITE_DEV_API_URL

// Dev namespaces the CSRF cookie (see settings.py) so other localhost projects,
// which share the port-agnostic localhost cookie jar, can't clobber it. Prod
// keeps the default name.
const csrfCookieName = import.meta.env.MODE === 'production' ? 'csrftoken' : 'trialytix_csrftoken'

// create axios instance with baseURL and predefined timeout
export const _axios = axios.create({
  url: apiUrl,
  baseURL: apiUrl,
  timeout: 300000,
  withCredentials: true,
  xsrfCookieName: csrfCookieName,
  xsrfHeaderName: 'X-CSRFToken'
})

function getCookie(name: string) {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift()
}

function registerAxiosRequestInterceptors() {
  // CSRF Token interceptor
  _axios.interceptors.request.use((config) => {
    if (config.headers) {
      config.headers['X-CSRFToken'] = getCookie(csrfCookieName) ?? ''
    }
    return config
  }, error => {
    return error
  })
  
  // JSON Schema validation interceptor
  _axios.interceptors.request.use(requestValidationInterceptor, error => Promise.reject(error))
}

// A single in-flight session check shared across concurrent 403s, so a burst
// of failed requests triggers only one whoami round-trip and one redirect.
let sessionCheck: Promise<boolean> | null = null

/**
 * Verifies whether the Django session is still authenticated by asking the
 * whoami endpoint (AllowAny — returns the user when logged in, `{}` when not).
 * Concurrent callers share the same in-flight request.
 * @returns true if a valid authenticated user is returned, false otherwise
 * @example
 * if (!(await isSessionStillAuthenticated())) redirectToLogin()
 */
function isSessionStillAuthenticated(): Promise<boolean> {
  if (!sessionCheck) {
    sessionCheck = _axios.get<{ id?: string }>('/api/auth/login')
      .then(response => !!response.data.id)
      .catch(() => false)
      .finally(() => { sessionCheck = null })
  }
  return sessionCheck
}

function registerAxiosResponseInterceptors(history: H.History) {
  // JSON Schema validation interceptor
  _axios.interceptors.response.use(responseValidationInterceptor, errorValidationInterceptor)

  // Authentication interceptor
  //
  // A 403 is ambiguous: DRF returns it both when the session is gone
  // (NotAuthenticated) and when the user merely lacks permission on a resource
  // (PermissionDenied) — and in production both collapse to a generic body, so
  // they cannot be told apart from the response alone. Clearing the session on
  // every 403 logs a valid user out on any permission error, so we confirm the
  // session is actually gone (via whoami) before wiping it and redirecting.
  _axios.interceptors.response.use((response) => response, async (error) => {
    const responseValid = !!error && !!error.response
    if (!responseValid || error.response.status !== 403) {
      return Promise.reject(error)
    }

    if (history.location.pathname.endsWith("/login")) {
      return Promise.reject(error)
    }

    const stillAuthenticated = await isSessionStillAuthenticated()
    if (stillAuthenticated) {
      // Permission denied on a resource — keep the user logged in.
      return Promise.reject(error)
    }

    localStorage.clear()
    const locArr = location.pathname.split('/').filter(path => path !== '')
    history.push("/login", { from: locArr[locArr.length - 1] })
    return Promise.reject(error)
  })
}

export function useAxiosInterceptors(history: H.History): void {
  registerAxiosRequestInterceptors()
  registerAxiosResponseInterceptors(history)
}

// defined methods in HttpClient
interface HttpMethods {
  get<R, P>(path: string, _params?: P | null): Promise<R>
  post<R, B>(path: string, body: B): Promise<R>
  put<R, B>(path: string, id: string | number | null | undefined, body: B): Promise<R>
  patch<R, B>(path: string, id: string | number | null, body: B): Promise<R>
  delete<R, B>(path: string, id: string | number | null, body?: B | undefined): Promise<R>
  uploadFile<R>(path: string, file?: File): Promise<R>
  downloadFile<P>(path: string, _params?: P | null): Promise<Blob | AxiosResponse<Blob>>
}

// exporting an object literal to keep KISS principle and avoid singletone boilerplate
export const HttpClient: HttpMethods = {
  async get<R, P>(path: string, _params: P | null = null) {
    const request: AxiosRequestConfig = {
      params: _params,
      withCredentials: true
    }

    return _axios.get<R>(path, request)
      .then((response) => {
        return response.data as R
      })
  },

  async post<R, B>(path: string, body: B) {
    return _axios.post<R>(path, body)
      .then((resp: AxiosResponse<R>) => resp.data as R)
  },

  async put<R, B>(path: string, id: string | number | null, body: B) {
    if (!id) {
      return _axios.put<R>(path, body)
      .then((resp) => resp.data as R)
    }
    return _axios.put<R>(`${path}${id}/`, body)
      .then((resp: AxiosResponse<R>) => resp.data as R)
  },

  async patch<R, B>(path: string, id: string | number | null, body: B) {
    if (!id) {
      return _axios.patch<R>(path, body)
      .then((resp: AxiosResponse<R>) => resp.data as R)
    }
    return _axios.patch<R>(`${path}${id}/`, body)
      .then((resp: AxiosResponse<R>) => resp.data as R)
  },

  async delete<R, B>(path: string, id: string | number | null, body?: B) {
    if (!id) {
      return _axios.delete<R>(`${path}/`, { data: body})
        .then((resp: AxiosResponse<R>) => resp.data as R)
    }
    return _axios.delete<R>(`${path}${id}/`)
      .then((resp: AxiosResponse<R>) => resp.data as R)
  },

  async uploadFile<R>(path: string, file: File) {
    const data = new FormData()
    data.append('dump', file)

    return _axios.post<R>(path, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    }).then(resp => resp.data as R)
  },

  async downloadFile<P>(path: string, _params: P | null = null) {
    return _axios.get<Blob>(path, { responseType: 'blob', params: _params })
      .then(async response => {
        let fileName: string = response.headers['content-disposition'].split('=')[1]
        if (fileName.includes('UTF-8')) {
          const encodedName = fileName.split("'")
          fileName = encodedName.length === 3 ? encodedName[2] : fileName
        }
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return response
    })
  }
}
